/**
 * React Query hooks for AI Response Drafts API
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { AIDraft, AIDraftListResponse } from '@/types/aiDraft';
import {
  listAIDrafts,
  getAIDraft,
  approveAIDraft,
  rejectAIDraft,
  sendAIDraft,
} from '../aiDrafts';

// Query keys
export const aiDraftKeys = {
  all: ['ai-drafts'] as const,
  lists: () => [...aiDraftKeys.all, 'list'] as const,
  list: (status?: string) => [...aiDraftKeys.lists(), { status }] as const,
  details: () => [...aiDraftKeys.all, 'detail'] as const,
  detail: (id: string) => [...aiDraftKeys.details(), id] as const,
};

/**
 * List AI drafts with optional status filter. Polls every 30s.
 */
export function useAIDrafts(status?: string): UseQueryResult<AIDraftListResponse, Error> {
  return useQuery({
    queryKey: aiDraftKeys.list(status),
    queryFn: () => listAIDrafts(status),
    refetchInterval: 30_000,
  });
}

/**
 * Get a single AI draft with full conversation context
 */
export function useAIDraft(id: string): UseQueryResult<AIDraft, Error> {
  return useQuery({
    queryKey: aiDraftKeys.detail(id),
    queryFn: () => getAIDraft(id),
    enabled: !!id,
  });
}

/**
 * Approve a draft (optionally with edited content), then send it
 */
export function useApproveAndSendAIDraft(): UseMutationResult<
  AIDraft,
  Error,
  { id: string; draftContent?: string },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, draftContent }) => {
      await approveAIDraft(id, draftContent);
      return sendAIDraft(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiDraftKeys.lists() });
    },
  });
}

/**
 * Approve a draft ONLY (SPEC FE-1 / R3c) - calls the existing per-draft approve
 * endpoint (PATCH /api/ai-drafts/{id}/approve) and NEVER chains into send.
 * Approving only marks the draft approved; it does NOT transmit. This preserves
 * the per-message human send gate for bulk-approve (bulk SEND is out of scope -
 * there is intentionally no bulk-send affordance and no auto-send-on-approve).
 */
export function useApproveAIDraft(): UseMutationResult<
  AIDraft,
  Error,
  { id: string; draftContent?: string },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, draftContent }) => approveAIDraft(id, draftContent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiDraftKeys.lists() });
    },
  });
}

/**
 * Bulk-approve drafts (SPEC FE-1 / R3c). Loops the existing per-draft approve
 * endpoint over the selected ids - NO new endpoint, and NEVER a send call. The
 * per-message human send gate stays intact: a bulk approve only marks drafts
 * approved; an operator still sends each one individually.
 */
export function useBulkApproveAIDrafts(): UseMutationResult<
  { approved: string[]; failed: string[] },
  Error,
  { ids: string[] },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids }) => {
      const approved: string[] = [];
      const failed: string[] = [];
      for (const id of ids) {
        try {
          await approveAIDraft(id); // approve ONLY - never sendAIDraft
          approved.push(id);
        } catch {
          failed.push(id);
        }
      }
      return { approved, failed };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiDraftKeys.lists() });
    },
  });
}

/**
 * Bulk-reject drafts (SPEC FE-1 / R3c). Loops the existing per-draft reject
 * endpoint over the selected ids with a shared reason - NO new endpoint.
 */
export function useBulkRejectAIDrafts(): UseMutationResult<
  { rejected: string[]; failed: string[] },
  Error,
  { ids: string[]; reason: string },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, reason }) => {
      const rejected: string[] = [];
      const failed: string[] = [];
      for (const id of ids) {
        try {
          await rejectAIDraft(id, reason);
          rejected.push(id);
        } catch {
          failed.push(id);
        }
      }
      return { rejected, failed };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiDraftKeys.lists() });
    },
  });
}

/**
 * Reject a draft with a reason
 */
export function useRejectAIDraft(): UseMutationResult<
  AIDraft,
  Error,
  { id: string; reason: string },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => rejectAIDraft(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiDraftKeys.lists() });
    },
  });
}
